// Worker transport (Caterpillar Monterrey). Bus stops are flat master data per
// location — there is no route/sequence layer in the API, so a stop is only a
// name and an active flag.

export interface BusStop {
	id: number;
	name: string;
	isActive: boolean;
}

export interface TransportAsyncState {
	status: "idle" | "pending" | "fulfilled" | "error";
	errorMessage: string;
}

export interface TransportState {
	busStops: BusStop[];
	// The location the loaded list belongs to. Kept so a list fetched for a
	// previously opened location is never rendered against the current one.
	busStopsLocationId: number | null;
	getBusStops: TransportAsyncState;
	createBusStop: TransportAsyncState;
	renameBusStop: TransportAsyncState;
	updateBusStopStatus: TransportAsyncState;

	// Worker side.
	// Whether transport runs at the worker's own location at all. Resolved once
	// per session and used to decide whether the app asks about a ride during
	// sign-up, and whether the Transport tab exists.
	isTransportEnabledForWorker: boolean | null;
	// Options for the shift currently being signed up for. Cleared between
	// shifts so a previous shift's stops are never offered against a new one.
	shiftTransportOptions: TransportOptions | null;
	shiftTransportOptionsShiftId: number | null;
	getShiftTransportOptions: TransportAsyncState;
	myTransportRequests: TransportRequest[];
	getMyTransportRequests: TransportAsyncState;
	cancelMyTransportRequest: TransportAsyncState;
	// The ride being cancelled, so only its own row shows a spinner.
	cancellingRequestId: number | null;

	// The standalone booking screen — a ride with no shift behind it. Kept
	// apart from shiftTransportOptions because the two are asked for at the
	// same time during a sign-up, and the standalone screen is the only place
	// the worker picks a direction for themselves.
	standaloneTransportOptions: TransportOptions | null;
	getStandaloneTransportOptions: TransportAsyncState;
	createTransportRequest: TransportAsyncState;
	// How many rides the last standalone booking created, so the confirmation
	// can say what happened: "both ways" over three dates is six rides.
	createdRequestCount: number;

	// The default-bus-stop settings screen. Kept separate from
	// shiftTransportOptions, which the sign-up flow clears between shifts.
	myLocationId: number | null;
	myLocationBusStops: BusStop[];
	getMyLocationBusStops: TransportAsyncState;
	myDefaultBusStop: DefaultBusStop | null;
	getMyDefaultBusStop: TransportAsyncState;
	setMyDefaultBusStop: TransportAsyncState;
	clearMyDefaultBusStop: TransportAsyncState;

	// Supervisor side.
	transportRequests: SupervisorTransportRequest[];
	getTransportRequests: TransportAsyncState;
	// Set when the list came back 403 — the caller has no access to the
	// location they asked for, which is a different thing to say than "no
	// rides". The API used to return the data.
	transportRequestsForbidden: boolean;
	// The approval queue's own call. Kept apart from getTransportRequests so a
	// decision in flight never blanks the list behind it.
	actionTransportRequests: TransportAsyncState;
	// Per-ride refusals from the last decision, keyed by ride id. A 200 does
	// not mean every ride was decided, so these are surfaced per row rather
	// than as one screen-level error.
	actionFailures: Record<number, string>;
	dailyOrder: TransportDailyOrderRow[];
	getDailyOrder: TransportAsyncState;
	transportLocations: TransportLocation[];
	getTransportLocations: TransportAsyncState;
}

export interface GetBusStopsPayload {
	locationId: number;
	includeInactive?: boolean;
}

export interface CreateBusStopPayload {
	locationId: number;
	name: string;
}

export interface RenameBusStopPayload {
	id: number;
	name: string;
	// Not sent — the rename endpoint is keyed on the stop id alone. Carried so
	// the thunk can refresh the right location's list afterwards.
	locationId: number;
}

export interface UpdateBusStopStatusPayload {
	id: number;
	isActive: boolean;
	locationId: number;
}

// A ride is always one leg. "B" exists only as an offer — on a shift, or as the
// worker's choice on a standalone booking — and always resolves into a separate
// pickup and drop-off. No booking ever comes back with a type of "B".
export type TransportType = "P" | "D" | "B";

export type TransportLegType = "P" | "D";

// What a new shift offers before the supervisor touches the field. Transport is
// worth having on by default where it exists — a shift created without a
// thought still carries both legs, and switching it off is the deliberate act.
export const DEFAULT_SHIFT_TRANSPORT_TYPE: TransportType = "B";

// Returned by the API so the labels arrive already localised. Never hardcode
// the three options from this file.
export interface TransportTypeOption {
	code: TransportType;
	name: string;
}

// Where the pre-selected stop came from, so the copy can be honest about how
// much it is worth trusting.
export type SuggestionSource = "default" | "lastUsed" | "none";

export interface TransportShiftInfo {
	startDateTime: string;
	endDateTime: string;
	jobName: string | null;
}

export interface TransportOptions {
	transportEnabled: boolean;
	locationId: number | null;
	busStops: BusStop[];
	// Narrowed to what the shift offers. For a shift this is informational —
	// the worker does not pick a direction.
	transportTypes: TransportTypeOption[];
	// One suggestion, not one per leg. Both the pickup and the drop-off picker
	// are pre-filled with it and each can then be changed.
	suggestedBusStopId: number | null;
	suggestionSource: SuggestionSource;
	// Only present when options was called with workerShiftId.
	shift: TransportShiftInfo | null;
}

// The worker's usual stop for one location, set from the transport settings
// screen. Always a suggestion, never binding — picking a different stop for
// one ride never changes it.
export interface DefaultBusStop {
	locationId: number;
	busStopId: number;
	busStopName: string;
}

export interface GetMyLocationBusStopsPayload {
	locationId?: number;
}

export interface GetMyDefaultBusStopPayload {
	locationId: number;
}

export interface SetMyDefaultBusStopPayload {
	locationId: number;
	busStopId: number;
}

export interface ClearMyDefaultBusStopPayload {
	locationId: number;
}

// A single-letter code on every ride, on every endpoint but the supplier order.
//
//   N  Requested — asked for, nobody has decided. serviceDateTime is null.
//   A  Approved  — the car is coming. serviceDateTime is set, and can move.
//   R  Rejected  — turned down. Final.
//   C  Cancelled — by the worker, a supervisor, or the shift going away. Final.
//
// N and A are live; R and C are final and the worker asks again rather than
// reopening.
export type TransportRequestStatus = "N" | "A" | "R" | "C";

export const LIVE_TRANSPORT_STATUSES: TransportRequestStatus[] = ["N", "A"];

export const isLiveTransportStatus = (
	status: TransportRequestStatus
): boolean => status === "N" || status === "A";

// R and C are the end of the road: nothing can be done to the ride, and the
// worker's next move is a fresh request.
export const isFinalTransportStatus = (
	status: TransportRequestStatus
): boolean => status === "R" || status === "C";

// The status filter the three list endpoints take, in place of the removed
// includeCancelled. A comma-separated list of codes, or "all".
export const ALL_TRANSPORT_STATUSES = "all";

export const toStatusFilter = (
	statuses: TransportRequestStatus[] | "all"
): string => (statuses === "all" ? ALL_TRANSPORT_STATUSES : statuses.join(","));

export interface TransportRequest {
	id: number;
	workerShiftId: number | null;
	busStopId: number;
	busStopName: string;
	// Never "B" — a both-ways booking is two rides from the moment it is made.
	transportType: TransportLegType;
	// The day the ride is for. A bare date, "2026-09-20", with no time and no
	// offset. Always present, even while the time is not.
	serviceDate: string;
	// When the car collects this worker — at their stop on a P, at the plant on
	// a D. There is no drop-off time anywhere in the model. Null until the ride
	// is approved: never fall back to the shift's own start or end.
	serviceDateTime: string | null;
	phoneNumber: string | null;
	status: TransportRequestStatus;
	// The approver's words, passed to the worker verbatim. On a rejection this
	// is the only thing telling them why.
	actionNote: string | null;
	actionedDate: string | null;
	// Null for a standalone booking — the signal to hide shift context.
	shift: TransportShiftInfo | null;
}

// Which decision a row's action button opens the review dialog on. A pending
// ride is reviewed — approved or rejected in one sitting — and an approved one
// can only have its time moved or be cancelled outright.
export type RideReviewMode = "review" | "reschedule" | "cancel";

// The supervisor list is the worker list with the rider identified.
export interface SupervisorTransportRequest extends TransportRequest {
	workerId: string;
	workerName: string;
	employeeId: string | null;
	// Null while the ride is still pending.
	actionedByName: string | null;
}

// A ride as it arrives inline on a shift — on GET /Workers/Shifts for the
// worker's own shifts, and on GET /Shifts/AcceptedWorkers for the supervisor's
// shift detail. Live rides only, and always an array: [] when none were booked,
// two entries when both legs were, possibly at different stops and in
// different states.
export interface ShiftTransportRide {
	id: number;
	shiftId: number;
	workerId: string;
	transportType: TransportLegType;
	busStopId: number;
	busStopName: string;
	serviceDate: string;
	serviceDateTime: string | null;
	status: TransportRequestStatus;
	actionNote: string | null;
}

// The supplier order. transportType and status are the localised human labels
// here, not the codes, because this output is exported and read by people.
// Shift columns are null for standalone bookings.
export interface TransportDailyOrderRow {
	serviceDate: string;
	serviceDateTime: string | null;
	transportType: string;
	workerName: string;
	employeeId: string | null;
	badgeNumber: string | null;
	phoneNumber: string | null;
	busStopName: string;
	costCenter: string | null;
	shiftStartDateTime: string | null;
	shiftEndDateTime: string | null;
	jobName: string | null;
	shiftPurpose: string | null;
	// "Requested" / "Approved" / "Rejected" / "Cancelled" — a word, not a code.
	status: string;
}

export interface TransportLocation {
	id: number;
	name: string;
}

// What the worker chose on the transport step, posted inside the sign-up. The
// stops are their only input: the direction comes from the shift and the time
// from approval. Send only the leg the shift's transportType actually needs.
export interface TransportSignupSelection {
	pickupBusStopId?: number;
	dropOffBusStopId?: number;
}

export interface GetTransportOptionsPayload {
	// Sign-up flow: the shift the worker is about to sign up for.
	shiftId?: number;
	// Wins over shiftId when both are sent.
	workerShiftId?: number;
	// Standalone. Omit all three and the worker's own location is used.
	locationId?: number;
}

// A standalone booking: no shift, so the worker gives the direction and the
// dates themselves. There are no times — a ride has none until it is approved.
// "B" is accepted here and splits server-side into a pickup and a drop-off per
// date, so three dates booked both ways is six rides.
export interface CreateTransportRequestPayload {
	locationId: number;
	transportType: TransportType;
	// Required when the direction includes that leg. The two may name the same
	// stop, or differ — being collected near home and set down elsewhere is a
	// normal thing to want.
	pickupBusStopId?: number;
	dropOffBusStopId?: number;
	// Local dates at the location, "yyyy-MM-dd". The stops apply to every date
	// in the batch: different stops on different days means separate bookings.
	dates: string[];
}

export interface GetMyTransportRequestsPayload {
	from?: string;
	to?: string;
	// Comma-separated codes, or "all". Defaults server-side to "N,A".
	status?: string;
}

export interface GetTransportRequestsPayload {
	locationId: number;
	from?: string;
	to?: string;
	// Comma-separated codes, or "all". Defaults server-side to "N,A"; pass "N"
	// for the approval queue alone.
	status?: string;
}

export interface GetTransportDailyOrderPayload {
	locationId: number;
	date: string;
	// Defaults server-side to "A" — approved rides only, which is what the
	// order is. "N" chases the undecided, "all" shows what dropped out.
	status?: string;
}

// A supervisor's decision. "A" both approves and re-times: approving an
// already-approved ride is how its time is changed, and the worker is told
// their ride has moved rather than that it is confirmed.
export type TransportDecision = "A" | "R" | "C";

export interface TransportActionItem {
	id: number;
	// Overrides the batch serviceDateTime for this one ride.
	serviceDateTime?: string;
	// Approve only. Moves this rider to another stop at the same location.
	busStopId?: number;
}

export interface TransportActionPayload {
	decision: TransportDecision;
	// Sent to the worker word for word, so it is written for them rather than
	// for an audit log.
	note?: string;
	// Required on "A". The batch default, applied to every item without its own.
	serviceDateTime?: string;
	// At least one. A single decision is a one-item list — batch is the normal
	// shape here, because the real action is "everyone on tomorrow's entrada
	// leaves at 04:40".
	items: TransportActionItem[];
}

// A ride the decision could not be applied to. The rest of the batch still
// went through, so this is shown against its own row rather than as a failure
// of the whole call.
export interface TransportActionFailure {
	id: number;
	reason: string;
}

export interface TransportActionResult {
	succeededCount: number;
	// Enough to refresh the queue in place rather than re-fetching the list —
	// but only the ride itself. The rider is NOT on these: the endpoint answers
	// with the plain ride model, so the worker's name, employee id and who took
	// the decision have to be kept from the row already held.
	updated: TransportRequest[];
	failed: TransportActionFailure[];
}
